import { useEffect } from 'react';
import { appThemeApiRef, useApi } from '@backstage/core-plugin-api';

// Default favicon paths
const DEFAULT_FAVICON = '/swbc-favicon.ico';
const DEFAULT_FAVICON_16 = '/swbc-favicon-16x16.png';
const DEFAULT_FAVICON_32 = '/swbc-favicon-32x32.png';
const DEFAULT_APPLE_TOUCH_ICON = '/apple-touch-icon.png';

// SWBC favicon paths - you'll need to add these files to your public directory
const SWBC_FAVICON = '/swbc-favicon.ico';
const SWBC_FAVICON_16 = '/swbc-favicon-16x16.png';
const SWBC_FAVICON_32 = '/swbc-favicon-32x32.png';
const SWBC_APPLE_TOUCH_ICON = '/swbc-apple-touch-icon.png';

/**
 * Component that dynamically changes the favicon based on the current theme
 */
export const DynamicFavicon = () => {
  const appThemeApi = useApi(appThemeApiRef);
  
  const updateFavicons = (themeId: string | undefined) => {
    // Update standard favicon
    const favicon = document.querySelector('link[rel="icon"][href*="favicon.ico"]') as HTMLLinkElement;
    if (favicon) {
      favicon.href = themeId === 'swbc' ? SWBC_FAVICON : DEFAULT_FAVICON;
    }
    
    // Update shortcut icon
    const shortcutIcon = document.querySelector('link[rel="shortcut icon"]') as HTMLLinkElement;
    if (shortcutIcon) {
      shortcutIcon.href = themeId === 'swbc' ? SWBC_FAVICON : DEFAULT_FAVICON;
    }
    
    // Update 16x16 favicon
    const favicon16 = document.querySelector('link[rel="icon"][sizes="16x16"]') as HTMLLinkElement;
    if (favicon16 && document.head.contains(favicon16)) {
      favicon16.href = themeId === 'swbc' ? (SWBC_FAVICON_16 || SWBC_FAVICON) : DEFAULT_FAVICON_16;
    }
    
    // Update 32x32 favicon
    const favicon32 = document.querySelector('link[rel="icon"][sizes="32x32"]') as HTMLLinkElement;
    if (favicon32 && document.head.contains(favicon32)) {
      favicon32.href = themeId === 'swbc' ? (SWBC_FAVICON_32 || SWBC_FAVICON) : DEFAULT_FAVICON_32;
    }
    
    // Update apple touch icon
    const appleIcon = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement;
    if (appleIcon && document.head.contains(appleIcon)) {
      appleIcon.href = themeId === 'swbc' ? (SWBC_APPLE_TOUCH_ICON || SWBC_FAVICON) : DEFAULT_APPLE_TOUCH_ICON;
    }
  };

  // Initial setup
  useEffect(() => {
    const themeId = appThemeApi.getActiveThemeId();
    updateFavicons(themeId);
  }, [appThemeApi]);

  // Subscribe to theme changes
  useEffect(() => {
    const subscription = appThemeApi.activeThemeId$().subscribe((themeId: string | undefined) => {
      updateFavicons(themeId);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [appThemeApi]);

  // This component doesn't render anything
  return null;
};
